import {
  Text,
  Box,
  Secondary,
  Inline,
  Strong,
  TextLinkButton,
  IconRecommended,
  Badge,
} from 'braid-design-system';
import { observer } from 'mobx-react';
import Skeleton from '../../common/Skeleton';
import translations from './.vocab/';
import { useTranslations } from '@vocab/react';
import { JobStatus } from '@seek/hirer-job-information';
import type { FC } from 'react';
import { useUserActionStatus } from 'src/components/common/UserActionStatusContext';

interface Props {
  status: JobStatus;
  numberOfDaysRemaining: number | null;
  billableApplicants?:
    | {
        success: true;
        count: number;
      }
    | {
        success: false;
      };
  internalReference?: string;
  bucketStatsTotalCount: number;
  openBillableDialog: () => void;
}

const JobStatDetail: FC<Props> = ({
  status,
  numberOfDaysRemaining,
  billableApplicants,
  internalReference,
  bucketStatsTotalCount,
  openBillableDialog,
}) => {
  const { t } = useTranslations(translations);

  const getJobExpiryItem = () => {
    if (numberOfDaysRemaining === null) {
      return undefined;
    }
    if (JobStatus.ACTIVE === status) {
      if (numberOfDaysRemaining === 0) {
        return <Text>{t('Expires today')}</Text>;
      }
      return (
        <Text>
          {t('Days left', {
            statNumber: numberOfDaysRemaining,
            Strong: (children) => <Strong>{children}</Strong>,
          })}
        </Text>
      );
    }
    return undefined;
  };

  const { getBillableNewFeatureBadgeStatus, hideBillableNewFeatureBadge } =
    useUserActionStatus('billable-new-feature-badge');

  return (
    <Layout>
      {getJobExpiryItem()}
      <Text>
        {t('Total applications', {
          statNumber: bucketStatsTotalCount, // FIXME: We shouldn't have undefined bucketStatsTotalCount
          Strong: (children) => <Strong>{children}</Strong>,
        })}
      </Text>

      {billableApplicants && (
        <Inline alignY="top" space="xsmall">
          <Text weight="medium">
            <TextLinkButton
              icon={<IconRecommended />}
              weight="weak"
              onClick={() => {
                openBillableDialog();
                hideBillableNewFeatureBadge();
              }}
            >
              <Strong>
                {billableApplicants.success
                  ? t('<Strong>statNumber</Strong> Billable applicants', {
                      statNumber: billableApplicants.count,
                    })
                  : t('Billable applicants')}
              </Strong>
            </TextLinkButton>
          </Text>
          {getBillableNewFeatureBadgeStatus() === 'visible' && (
            <Badge tone="positive">{t('New feature')}</Badge>
          )}
        </Inline>
      )}

      {internalReference ? (
        <Text weight="medium">
          {t('Internal Reference', {
            Reference: () => <Secondary>{internalReference}</Secondary>,
          })}
        </Text>
      ) : null}
    </Layout>
  );
};

const Layout: FC<{
  children: Array<JSX.Element | undefined | null | false>;
}> = ({ children }) => (
  <Box data-testid="job-stats-summary">
    <Inline space="small" alignY="top">
      {children}
    </Inline>
  </Box>
);

export const JobStatDetailSkeleton: FC = () => {
  const { t } = useTranslations(translations);

  return (
    <Layout>
      <Text>
        {t('Days left', {
          statNumber: -1, // FIXME: We shouldn't have pass in -1 as loading state
          Strong: (children) => (
            <Skeleton loading={true} numberOfCharacters={8}>
              {children}
            </Skeleton>
          ),
        })}
      </Text>
      <Text>
        {t('Total applications', {
          statNumber: -1, // FIXME: We shouldn't have pass in -1 as loading state
          Strong: (children) => (
            <Skeleton loading={true} numberOfCharacters={8}>
              {children}
            </Skeleton>
          ),
        })}
      </Text>
    </Layout>
  );
};

export default observer(JobStatDetail);
